import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Check, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CaseStudyDossierModal — lead-capture gate for the combined case-study dossier
 * download. Submits to /api/content/case-studies/lead, emails the requester the
 * dossier (and notifies the team), then offers an immediate download.
 */
const CaseStudyDossierModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", role: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setForm({ full_name: "", email: "", organisation: "", role: "", consent: false }); setDone(false); }
  }, [open]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please confirm consent before downloading."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/content/case-studies/lead`, { ...form });
      setDone(true);
      toast.success("Dossier on its way — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto" role="dialog" aria-modal="true" data-testid="dossier-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white max-w-xl w-full border-t-4 border-[#7737F6] shadow-2xl">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-black p-2" aria-label="Close" data-testid="dossier-modal-close"><X size={20} /></button>
        <div className="p-8 md:p-10">
          {done ? (
            <div className="text-center py-4" data-testid="dossier-success">
              <div className="w-14 h-14 mx-auto bg-[#7737F6] text-white flex items-center justify-center mb-6"><Check size={28} strokeWidth={2} /></div>
              <h3 className="ek-heading text-2xl md:text-3xl text-black">Dossier on its way.</h3>
              <p className="ek-body mt-4">We've emailed you the full case study dossier. You can also download it now.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`${API}/content/case-studies-dossier.pdf`} className="ek-btn ek-btn-primary" data-testid="dossier-download-now"><Download size={16} /> Download now</a>
                <button type="button" onClick={onClose} className="ek-btn ek-btn-outline-dark" data-testid="dossier-close-after-success">Close</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#7737F6] mb-2"><Download size={14} /><span className="ek-eyebrow !text-[11px]">Case study dossier</span></div>
              <h3 className="ek-heading text-2xl md:text-[26px] text-black leading-tight" data-testid="dossier-modal-title">Download all case studies</h3>
              <p className="ek-body text-[15px] mt-3">Tell us a little about yourself and we'll email you the full dossier — every case study in one branded PDF.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="dossier-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label htmlFor="d_name" className="ek-label">Full name</label><input id="d_name" name="full_name" type="text" required value={form.full_name} onChange={onChange} className="ek-input" data-testid="dossier-input-name" /></div>
                  <div><label htmlFor="d_email" className="ek-label">Work email</label><input id="d_email" name="email" type="email" required value={form.email} onChange={onChange} className="ek-input" data-testid="dossier-input-email" /></div>
                  <div><label htmlFor="d_org" className="ek-label">Organisation</label><input id="d_org" name="organisation" type="text" required value={form.organisation} onChange={onChange} className="ek-input" data-testid="dossier-input-organisation" /></div>
                  <div><label htmlFor="d_role" className="ek-label">Role</label><input id="d_role" name="role" type="text" required value={form.role} onChange={onChange} className="ek-input" data-testid="dossier-input-role" /></div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer mt-2">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} required className="mt-1 w-4 h-4 accent-[#7737F6]" data-testid="dossier-input-consent" />
                  <span className="text-[13px] text-zinc-700 font-light leading-relaxed">I agree to Ekselens Consulting contacting me about these case studies. See our <a href="/privacy" target="_blank" rel="noreferrer" className="underline">privacy policy</a>.</span>
                </label>
                <button type="submit" disabled={submitting} className="ek-btn ek-btn-primary w-full justify-center disabled:opacity-60" data-testid="dossier-submit-button">
                  {submitting ? "Sending…" : <>Send me the dossier <Download size={16} /></>}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyDossierModal;
